import React, { useState, useEffect } from "react";
// Assuming you'll reuse or create a similar CSS file for the register form
// import "../style/css/RegisterForm.css";
// We'll keep the direct React state approach, as used in your LoginForm
import { useNavigate } from "react-router-dom";
// Assuming you have an AuthContext that exports a register function
import { useAuth } from "../AuthContext";

// --- Country Code Data Placeholder ---
// In a real application, you might use a library or fetch this data.
const initialCountryCodes = [
  // { code: "+1", label: "🇺🇸 +1" },
  { code: "+52", label: "🇲🇽 +52" },
  // { code: "+34", label: "🇪🇸 +34" },
  // { code: "+54", label: "🇦🇷 +54" },
  // Add more codes as needed
];

const planOptions = [
  { label: "Básico", value: "basic" },
  { label: "Profesional", value: "professional" },
  { label: "Clínica", value: "clinic" },
];

// Define the shape of the form values
interface RegisterFormValues {
  fullName: string;
  username: string;
  mobileCode: string;
  mobileNumber: string;
  password: string;
  confirmPassword: string;
  plan: string; // The new field for the selected plan
  email: string;
}

// Define the shape of the validation errors
type FormErrors = Partial<Record<keyof RegisterFormValues, string>>;

// --- RegisterForm Component ---
export function RegisterForm() {
  const { register } = useAuth(); // Uncomment and implement the register function
  const navigate = useNavigate();

  // --- State Initialization ---
  const [values, setValues] = useState<RegisterFormValues>({
    fullName: "",
    username: "",
    mobileCode: initialCountryCodes[0].code,
    mobileNumber: "",
    password: "",
    confirmPassword: "",
    plan: "", // Initialize plan to empty or the first option's value
    email: "", // In useState initialization
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [registerError, setRegisterError] = useState<string | null>(null);
  const [userTimezone, setUserTimezone] = useState("");
  const [userCurrency, setUserCurrency] = useState("");

  useEffect(() => {
    // Get timezone (very reliable in browsers)
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    setUserTimezone(timezone);

    // Get currency from locale (less reliable but good guess)
    const currency = new Intl.NumberFormat().resolvedOptions().currency;
    if (currency) {
      setUserCurrency(currency);
    } else {
      // Fallback: guess currency from country code
      const countryToCurrency: { [key: string]: string } = {
        MX: "MXN",
        US: "USD",
        ES: "EUR",
        // Add more mappings as needed
      };

      // Extract country from phone code (simplified)
      const phoneCodeToCountry: { [key: string]: string } = {
        "+52": "MX",
        "+1": "US",
        "+34": "ES",
        // Add more mappings
      };

      const country = phoneCodeToCountry[values.mobileCode];
      if (country && countryToCurrency[country]) {
        setUserCurrency(countryToCurrency[country]);
      }
    }
  }, [values.mobileCode]);

  // --- Handlers ---

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    // Clear errors and update state
    setErrors((prev) => ({ ...prev, [name]: undefined }));
    setRegisterError(null);
    setValues((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validate = (currentValues: RegisterFormValues): FormErrors => {
    const nextErrors: FormErrors = {};

    // --- Existing Validations ---
    if (!currentValues.fullName.trim()) {
      nextErrors.fullName = "El nombre completo es obligatorio.";
    }

    if (!currentValues.username.trim()) {
      nextErrors.username = "El nombre de usuario es obligatorio.";
    } else if (currentValues.username.length < 3) {
      nextErrors.username = "El usuario debe tener al menos 3 caracteres.";
    }
    // Email validation in validate function
    if (!currentValues.email.trim()) {
      nextErrors.email = "El correo electrónico es obligatorio.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(currentValues.email)) {
      nextErrors.email = "Ingresa un correo electrónico válido.";
    }

    if (!currentValues.password) {
      nextErrors.password = "La contraseña es obligatoria.";
    } else if (currentValues.password.length < 8) {
      nextErrors.password = "La contraseña debe tener al menos 8 caracteres.";
    }

    if (!currentValues.confirmPassword) {
      nextErrors.confirmPassword = "Debes confirmar la contraseña.";
    } else if (currentValues.confirmPassword !== currentValues.password) {
      nextErrors.confirmPassword = "Las contraseñas no coinciden.";
    }

    if (!currentValues.mobileNumber.trim()) {
      nextErrors.mobileNumber = "El número de teléfono es obligatorio.";
    } else if (!/^\d+$/.test(currentValues.mobileNumber.trim())) {
      nextErrors.mobileNumber = "Ingresa solo dígitos.";
    } else if (currentValues.mobileNumber.length < 10) {
      nextErrors.mobileNumber = "El teléfono debe tener al menos 10 dígitos.";
    }

    // --- NEW Plan Validation ---
    if (!currentValues.plan) {
      nextErrors.plan = "Debes seleccionar un plan.";
    }

    return nextErrors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validationErrors = validate(values);
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      return;
    }

    setIsSubmitting(true);
    setRegisterError(null);

    // Prepare data for the API
    const registrationData = {
      fullName: values.fullName,
      username: values.username,
      phoneNumber: values.mobileCode + values.mobileNumber,
      password: values.password,
      plan: values.plan, // This will be the English value ('basic', 'professional', or 'clinic')
      email: values.email, // In registrationData object
      timezone: userTimezone, // Send detected timezone
      currency: userCurrency, // Send detected currency
      countryCode: values.mobileCode, // Send the selected country code
    };

    // --- Simulated Registration Logic ---
    try {
      await register(registrationData);

      // Simulate API call delay
      await new Promise((resolve) => setTimeout(resolve, 1500));
      // Navigate to a success page or the dashboard
      navigate("/registration-success");
    } catch (error: any) {
      // Check if the error object has the 'data' property we threw
      if (error && error.data) {
        const apiResponse = error.data;

        // 1. Check for a single, general error message (Django often uses 'detail')
        const generalError = apiResponse.detail || apiResponse.error;

        if (generalError && typeof generalError === "string") {
          // Display the single API message
          setRegisterError(generalError);
        }

        // 2. Handle field-specific errors (like unique constraint violation)
        else if (
          typeof apiResponse === "object" &&
          !Array.isArray(apiResponse)
        ) {
          const newErrors: FormErrors = {};
          let topMessage: string | null = null;

          for (const key in apiResponse) {
            // Assuming Django REST Framework sends array of strings for errors
            const errorMessages = Array.isArray(apiResponse[key])
              ? apiResponse[key]
              : [apiResponse[key]];

            if (key in values) {
              // Map field errors back to the input field
              newErrors[key as keyof RegisterFormValues] = errorMessages[0];
            } else {
              // Collect other top-level messages (e.g., non_field_errors)
              topMessage = errorMessages[0];
            }
          }

          // Set field errors (e.g., username taken)
          setErrors((prev) => ({ ...prev, ...newErrors }));

          // If there's a top-level message not attached to a field, show it generally
          if (topMessage) {
            setRegisterError(topMessage);
          } else if (Object.keys(newErrors).length === 0) {
            // Fallback for an unexpected object structure
            setRegisterError(
              "El servidor devolvió un error de validación inesperado.",
            );
          }
        } else {
          // Fallback for an unexpected structure
          setRegisterError(
            "Ocurrió un error al registrar el usuario. Por favor, intenta de nuevo.",
          );
        }
      } else {
        // This usually catches network failures, CORS issues, or errors
        // thrown before the fetch successfully completed.
        setRegisterError(
          "Error de conexión. Revisa tu red o intenta más tarde.",
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // --- Render ---
  return (
    <div className="login-page-container">
      <div className="login-auth-container">
        <h2 className="text-center" style={{ marginBottom: "2rem" }}>
          <i
            className="fas fa-calendar-check"
            aria-hidden="true"
            style={{ color: "var(--color-accent)" }}
          ></i>{" "}
          Cara de agenda
        </h2>

        <div className="login-auth-card">
          <h3 style={{ marginBottom: "1.5rem", textAlign: "center" }}>
            Crear Cuenta
          </h3>

          {registerError && (
            <div className="login-message-box login-message-error">
              <i className="fas fa-times-circle"></i>
              {registerError}
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate>
            {/* Full Name Field */}
            <div className="login-form-group">
              <label htmlFor="fullName">Nombre Completo</label>
              <input
                type="text"
                id="fullName"
                name="fullName"
                value={values.fullName}
                onChange={handleChange}
                required
                placeholder="Tu nombre completo"
              />
              {errors.fullName && (
                <div className="error-message">{errors.fullName}</div>
              )}
            </div>

            {/* Username Field */}
            <div className="login-form-group">
              <label htmlFor="username">Nombre de Usuario</label>
              <input
                type="text"
                id="username"
                name="username"
                value={values.username}
                onChange={handleChange}
                required
                placeholder="Elige un nombre de usuario"
              />
              {errors.username && (
                <div className="error-message">{errors.username}</div>
              )}
            </div>
            {/* Email Field */}
            <div className="login-form-group">
              <label htmlFor="email">Correo Electrónico</label>
              <input
                type="email"
                id="email"
                name="email"
                value={values.email}
                onChange={handleChange}
                required
                placeholder="tu.correo@ejemplo.com"
              />
              {errors.email && (
                <div className="error-message">{errors.email}</div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="plan">Selecciona tu Plan</label>
              <select
                id="plan"
                name="plan"
                value={values.plan}
                onChange={handleChange}
                required
              >
                <option value="" disabled>
                  -- Seleccionar Plan --
                </option>
                {planOptions.map((option) => (
                  // The user sees the label (Spanish), but the value is the English one for the API
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {errors.plan && (
                <div className="error-message">{errors.plan}</div>
              )}
            </div>

            {/* Mobile Phone Field */}
            <div className="login-form-group">
              <label htmlFor="mobileNumber">Teléfono Móvil</label>
              <div style={{ display: "flex", gap: "0.5rem" }}>
                {/* Country Code Selector */}
                <select
                  id="mobileCode"
                  name="mobileCode"
                  value={values.mobileCode}
                  onChange={handleChange}
                  required
                  style={{ flexShrink: 0, paddingRight: "0.5rem" }}
                >
                  {initialCountryCodes.map((country) => (
                    <option key={country.code} value={country.code}>
                      {country.label}
                    </option>
                  ))}
                </select>

                {/* Mobile Number Input */}
                <input
                  type="tel"
                  id="mobileNumber"
                  name="mobileNumber"
                  value={values.mobileNumber}
                  onChange={handleChange}
                  required
                  placeholder="Número (solo dígitos)"
                  style={{ flexGrow: 1 }}
                />
              </div>
              {errors.mobileNumber && (
                <div className="error-message">{errors.mobileNumber}</div>
              )}
            </div>

            {/* Password Field */}
            <div className="login-form-group">
              <label htmlFor="password">Contraseña</label>
              <input
                type="password"
                id="password"
                name="password"
                value={values.password}
                onChange={handleChange}
                required
                placeholder="Ingresa tu contraseña"
              />
              {errors.password && (
                <div className="error-message">{errors.password}</div>
              )}
            </div>

            {/* Confirm Password Field */}
            <div className="login-form-group">
              <label htmlFor="confirmPassword">Confirmar Contraseña</label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                value={values.confirmPassword}
                onChange={handleChange}
                required
                placeholder="Repite tu contraseña"
              />
              {errors.confirmPassword && (
                <div className="error-message">{errors.confirmPassword}</div>
              )}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              className="btn btn--primary btn--large"
              style={{ width: "100%", marginTop: "1rem" }}
              disabled={isSubmitting}
            >
              <i
                className={`fas fa-user-plus ${
                  isSubmitting ? "fa-spinner fa-spin" : ""
                }`}
              ></i>
              {isSubmitting ? "Registrando..." : "Registrar"}
            </button>
          </form>

          <p
            className="text-center"
            style={{ marginTop: "1.5rem", fontSize: "0.95rem" }}
          >
            ¿Ya tienes cuenta? <a href="/login">Iniciar Sesión</a>
          </p>
        </div>
      </div>
    </div>
  );
}

export default RegisterForm;
